import {hasSeenResults, markResultsSeen, resultVisitKey} from "../../utils/groupResultVisit";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {motion} from "motion/react";
import {PlaneIcon} from "../../assets/svgComponents/PlaneIcon";
import {FarewiseIconLight} from "../../assets/svgComponents/FarewiseIconLight";
import WorldGlobeLarge from "../../assets/images/WorldGlobeLarge.png";
import FullCloudLeft from "../../assets/images/FullCloudSmall.png";
import FullCloudRight from "../../assets/images/FullCloud.png";

type Props = {
    /** Optional: Session ID direkt per Prop übergeben. Sonst wird :sessionId aus der URL gelesen. */
    sessionId?: string;
    /** Optional: Custom Handler (überschreibt die Default-Navigation). */
    onUnveil?: () => void;
    /** Optional: Falls du zu einem festen Pfad navigieren willst (überschreibt default `/sessions/:sessionId/results`) */
    nextPath?: string;
};

export default function ResultsUnveilPage(props: Props) {
    const params = useParams();
    const sessionId = props.sessionId || params.sessionId;
    const visitKey = sessionId ? resultVisitKey(sessionId) : '';
    return <UnveilVisit key={visitKey} {...props} sessionId={sessionId} visitKey={visitKey}/>;
}

function UnveilVisit({sessionId, onUnveil, nextPath, visitKey}: Props & {visitKey: string}) {
    const navigate = useNavigate();
    // Snapshot once per visit: StrictMode's repeated effects must not skip the first reveal.
    const [alreadySeen] = useState(() => !!visitKey && hasSeenResults(visitKey));

    const goNext = useCallback(() => {
        if (onUnveil) return onUnveil();
        if (nextPath) return navigate(nextPath, {replace: true});
        if (sessionId) return navigate(`/sessions/${sessionId}/results`, {replace: true});
        return navigate("/"); // Fallback
    }, [onUnveil, nextPath, navigate, sessionId]);

    useEffect(() => {
        if (alreadySeen) goNext();
        else if (visitKey) markResultsSeen(visitKey);
    }, [alreadySeen, visitKey, goNext]);

    // Tastatur: Enter/Space = Unveil
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [goNext]);

    if (alreadySeen) return null;

    return (
        <motion.div
            className="bg-gradient-to-b from-[#f6f8ff] from-[43.912%] to-[#dde5ff] w-full h-full min-h-screen flex flex-col relative overflow-hidden"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{duration: 0.6}}
            data-name="Results Unveil Page"
        >
            <CloudLeft/>
            <CloudRight/>
            {/* Top-right app icon: one step back */}
            <button
                type="button"
                aria-label="Back"
                className="absolute left-28 top-20 z-50 hover:scale-105"
                onClick={() => navigate(`/sessions`)}
            >
                <FarewiseIconLight/>
            </button>

            <div className="flex flex-col items-center px-8 pt-10 pb-16 flex-1">
                <MainContent/>
                <div className="flex items-center justify-center mt-[80px] z-20">
                    <UnveilButton onClick={goNext}/>
                </div>
            </div>
        </motion.div>
    );
}

/* ---------- Presentational Subcomponents ---------- */

function MainContent() {
    return (
        <motion.div
            className="flex flex-col items-center text-center"
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            transition={{duration: 0.8, delay: 0.4}}
        >
            <motion.div
                className="bg-[position:0%_0%,_50%_50%] bg-size-[auto,cover] size-[315px] rounded-2xl overflow-hidden mb-12 -mt-[50px]"
                aria-hidden
                style={{
                    backgroundImage: `url('data:image/svg+xml;utf8,<svg viewBox=\\'0 0 315 315\\' xmlns=\\'http://www.w3.org/2000/svg\\' preserveAspectRatio=\\'none\\'><rect x=\\'0\\' y=\\'0\\' height=\\'100%\\' width=\\'100%\\' fill=\\'url(%23grad)\\' opacity=\\'1\\'/><defs><radialGradient id=\\'grad\\' gradientUnits=\\'userSpaceOnUse\\' cx=\\'0\\' cy=\\'0\\' r=\\'10\\' gradientTransform=\\'matrix(-2.7313e-14 10.855 -10.855 2.8642e-14 157.5 157.5)\\'><stop stop-color=\\'rgba(246,248,255,0)\\' offset=\\'0\\'/><stop stop-color=\\'rgba(246,248,255,1)\\' offset=\\'1\\'/></radialGradient></defs></svg>'), url('${WorldGlobeLarge}')`,
                }}
                animate={{rotate: [0, 2, -2, 0]}}
                transition={{duration: 6, repeat: Infinity, ease: "easeInOut"}}
            />
            <motion.div
                className="font-['Outfit',_sans-serif] font-bold not-italic text-[#232323] text-[125px] text-center text-nowrap tracking-[-2.75px] -mt-[90px] z-20"
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.8, delay: 0.6}}
            >
                <motion.p
                    className="leading-[108.333px] mb-0"
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 0.8}}
                >
                    We found
                </motion.p>
                <motion.p
                    className="leading-[108.333px]"
                    initial={{opacity: 0, y: 20}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.6, delay: 1.0}}
                >
          <span
              className="bg-clip-text bg-gradient-to-b from-[#e7defe] to-[#b282e5]"
              style={{WebkitTextFillColor: "transparent"}}
          >
            the
          </span>
                    <span>{` spot.`}</span>
                </motion.p>
            </motion.div>
        </motion.div>
    );
}

function UnveilButton({onClick}: { onClick?: () => void }) {
    return (
        <motion.button
            type="button"
            className="bg-[#b282e5] box-border content-stretch flex gap-[13.696px] items-center justify-center pl-[29.167px] pr-[26.667px] py-[15.978px] relative rounded-[26.25px] shrink-0 w-[150px] cursor-pointer hover:bg-[#9f70d9] transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b282e5]/60"
            onClick={onClick}
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 1.2}}
            whileHover={{scale: 1.05}}
            whileTap={{scale: 0.95}}
            aria-label="Unveil results"
        >
            <div className="relative -ml-1">
                <PlaneIcon/>
            </div>
            <div
                className="font-['Space_Grotesk',_sans-serif] font-bold leading-[0] text-[#f6f8ff] text-[15.978px] tracking-[-0.8788px] -ml-0.5">
                Unveil
            </div>
        </motion.button>
    );
}

function CloudLeft() {
    return (
        <motion.div
            className="absolute left-60 top-56 h-[127.476px] w-[171.667px] z-10 -translate-x-[460px]"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            aria-hidden
        >
            <motion.div
                className="absolute bottom-[-26.67%] left-[-4.42%] right-[-4.61%] top-0"
                animate={{y: [0, -10, 0]}}
                transition={{duration: 4, repeat: Infinity, ease: "easeInOut"}}
            >
                <img className="block max-w-none size-full" src={FullCloudLeft} alt="" />
            </motion.div>
        </motion.div>
    );
}

function CloudRight() {
    return (
        <motion.div
            className="absolute right-72 top-80 h-[100.182px] w-[134.912px] z-10 translate-x-[220px]"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.3}}
            aria-hidden
        >
            <motion.div
                className="absolute bottom-[-26.67%] left-[-4.42%] right-[-4.61%] top-0"
                animate={{y: [0, 12, 0]}}
                transition={{duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2}}
            >
                <img className="block max-w-none size-full" src={FullCloudRight} alt="" />
            </motion.div>
        </motion.div>
    );
}
